import { Card, LiteralText, InlineMessage } from '../../components';
import actionCreator from '../../actioncreators/accountSettings/switchClients';
import React, { Component } from 'react';

const buildErrorMessage = (error, fallback) => {
  const message = error.message || fallback;
  const { code } = error;
  const traceId =
    error.errors && error.errors[0] && error.errors[0].details && error.errors[0].details.traceId;
  const parts = [message];

  if (code && code !== 'UNKNOWN_ERROR') {
    parts.push(`(code: ${code})`);
  }
  if (traceId) {
    parts.push(`(trace: ${traceId})`);
  }
  return parts.join(' ');
};

import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Pill, Loader, NotValidatedInput, PageHeader, StandardParagraph } from '@idm/ui-components';
import './style.scss';

const PlaceholderCard = () => (
  <div className="placeholder-card">
    <div className="placeholder-avatar" />
    <div className="placeholder-content">
      <div className="placeholder-line placeholder-line-main" />
      <div className="placeholder-line placeholder-line-sub" />
    </div>
  </div>
);

class ClientManagement extends Component {
  static propTypes = {
    associates: PropTypes.arrayOf(
      PropTypes.shape({
        associateId: PropTypes.string,
        clientId: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        client: PropTypes.object,
      })
    ).isRequired,
    isLoadingData: PropTypes.bool.isRequired,
    isAuthenticatedByNexo: PropTypes.bool.isRequired,
    userRoles: PropTypes.arrayOf(PropTypes.object),
    dispatchLoadData: PropTypes.func.isRequired,
    dispatchResetState: PropTypes.func.isRequired,
    history: PropTypes.object.isRequired,
  };

  static defaultProps = {
    userRoles: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      searchTerm: '',
      isSwitchingClient: false,
      switchClientError: null,
    };
  }

  componentDidMount() {
    const { dispatchLoadData, associates } = this.props;

    document.title = 'Lyric Client Management';

    if (associates.length === 0) {
      dispatchLoadData();
    }
  }

  componentWillUnmount() {
    const { dispatchResetState } = this.props;

    document.title = 'Lyric Login';
    dispatchResetState();
  }

  hasImpersonationRole = () => {
    const { userRoles } = this.props;

    return userRoles.some(
      (role) => (role.role === 'impersonator' || role.role === 'impersonate') && role.isAssigned
    );
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  handleImpersonateClient = async (associateId, clientId) => {
    this.setState({ isSwitchingClient: true, switchClientError: null });

    try {
      this.props.history.push(`/auth/client-management/select-associate/${clientId}`);
    } catch (error) {
      this.setState({
        isSwitchingClient: false,
        switchClientError: buildErrorMessage(error, 'Failed to switch client'),
      });
    }
  };

  toClientCardProps = (assoc) => {
    const baseProps = {
      avatarText: <LiteralText>{assoc.client.name[0]}</LiteralText>,
      mainText: <LiteralText>{assoc.client.name}</LiteralText>,
      active: false,
      associateId: assoc.associateId,
      clientId: assoc.clientId,
      clientTypeText: (
        <Pill
          color={assoc.isNexoClient ? 'purple3' : 'koala'}
          pillText={assoc.isNexoClient ? 'ADP Login' : 'Lyric HCM Login'}
        />
      ),
      displayclientTypeText: assoc.isNexoClient !== undefined,
    };

    if (this.hasImpersonationRole()) {
      return {
        ...baseProps,
        actionText: 'Impersonate',
        onActionClick: () => this.handleImpersonateClient(assoc.associateId, assoc.clientId),
      };
    }

    return baseProps;
  };

  getFilteredClients = () => {
    const { associates } = this.props;
    const { searchTerm } = this.state;

    // Deduplicate clients - same client can appear multiple times with different associates
    const uniqueClientsMap = new Map();

    associates.forEach((assoc) => {
      if (!uniqueClientsMap.has(assoc.clientId)) {
        uniqueClientsMap.set(assoc.clientId, assoc);
      }
    });

    const uniqueClients = Array.from(uniqueClientsMap.values());

    if (!searchTerm) {
      return uniqueClients;
    }

    return uniqueClients.filter((assoc) =>
      assoc.client.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  render() {
    const { isLoadingData } = this.props;
    const { searchTerm, isSwitchingClient, switchClientError } = this.state;

    if (!this.hasImpersonationRole()) {
      return null;
    }

    if (isLoadingData) {
      return (
        <div className="client-management">
          <PageHeader>Client Management</PageHeader>
          <StandardParagraph className="header-description">
            Select a client to impersonate and access their account
          </StandardParagraph>
          <div className="search-section">
            <NotValidatedInput
              type="text"
              placeholder="Search by client name..."
              value=""
              disabled={true}
              label="Search Clients"
              name="clientSearch"
            />
          </div>
          <div className="loading-placeholders">
            <PlaceholderCard />
            <PlaceholderCard />
            <PlaceholderCard />
          </div>
        </div>
      );
    }

    const filteredClients = this.getFilteredClients();
    const clientCards = filteredClients
      .sort((first, second) =>
        first.client.name.toLowerCase() > second.client.name.toLowerCase() ? 1 : -1
      )
      .map(this.toClientCardProps)
      .map((cardProps) => <Card key={cardProps.clientId} {...cardProps} />);

    return (
      <div className="client-management">
        <PageHeader>Client Management</PageHeader>
        <StandardParagraph className="header-description">
          Select a client to impersonate and access their account
        </StandardParagraph>

        {isSwitchingClient && (
          <div className="loading-overlay">
            <Loader size="small" />
            <StandardParagraph>Switching client context...</StandardParagraph>
          </div>
        )}

        {switchClientError && (
          <InlineMessage type="error" className="switch-client-error">
            {switchClientError}
          </InlineMessage>
        )}

        <div className="search-section">
          <NotValidatedInput
            type="text"
            placeholder="Search by client name..."
            value={searchTerm}
            onChange={this.handleSearchChange}
            disabled={isSwitchingClient}
            label="Search Clients"
            name="clientSearch"
          />
        </div>

        <div className="clients-container">
          {filteredClients.length === 0 ? (
            <div className="no-results">
              <div className="empty-state-icon">🔍</div>
              <PageHeader>No Clients Found</PageHeader>
              <StandardParagraph>
                {searchTerm
                  ? `No clients match "${searchTerm}". Try a different search term.`
                  : 'No clients available for impersonation.'}
              </StandardParagraph>
            </div>
          ) : (
            <>
              <div className="results-info">
                <StandardParagraph>
                  Showing {filteredClients.length} client{filteredClients.length !== 1 ? 's' : ''}
                </StandardParagraph>
              </div>
              <div className="current-associates">{clientCards}</div>
            </>
          )}
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    associates: state.accountSettingsSwitchClients.main.associates || [],
    isLoadingData: state.accountSettingsSwitchClients.loadData.isLoadingData || false,
    isAuthenticatedByNexo: state.me.main.isAuthenticatedByNexo || false,
    userRoles: state.me.main.roles || [],
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dispatchResetState() {
      dispatch(actionCreator.resetState());
    },
    dispatchLoadData() {
      dispatch(actionCreator.loadData());
    },
  };
}

export { ClientManagement, mapStateToProps, mapDispatchToProps };
export default connect(mapStateToProps, mapDispatchToProps)(ClientManagement);
